import React, { useState, useEffect, useMemo } from "react";
import { ref, get } from "firebase/database";
import { db } from "../../firebase";
import "../../styles/ResultadosTotales.css";

// signals_history vive en RTDB. Filtramos client-side por
// telegram_sent === true + result IN {WIN, LOSS}.
export default function ResultadosTotales() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab activo: "enviados" | "todos" | "descartados"
  const [activeTab, setActiveTab] = useState("enviados");

  // Filtros
  const [filterLiga, setFilterLiga] = useState("Todas");
  const [filterPeriodo, setFilterPeriodo] = useState("Todos");
  const [filterStake, setFilterStake] = useState("Todos");
  const [filterResult, setFilterResult] = useState("Todos");
  const [sortBy, setSortBy] = useState("started_at_desc");

  // Cargar signals_history desde RTDB
  useEffect(() => {
    // Admin: necesita el nodo COMPLETO (la pestaña "descartados" usa
    // telegram_sent !== true, incluye shadow) → NO se filtra con query.
    // get() una sola vez: vista de stats, no necesita realtime.
    const signalsRef = ref(db, "signals_history");
    let cancelled = false;
    get(signalsRef)
      .then(snap => {
        if (cancelled) return;
        const data = snap.val();
        if (!data) {
          setSignals([]);
        } else {
          const rows = Object.entries(data)
            .map(([id, v]) => ({ id, ...v }))
            .filter(r => r && typeof r === "object");
          setSignals(rows);
        }
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("[ResultadosTotales] RTDB error:", err);
        setError("No se pudieron cargar los resultados");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Helpers schema motor V6
  const getTs = r =>
    typeof r.started_at === "number" ? r.started_at : new Date(r.started_at).getTime();
  const getPick = r =>
    r.signal_type === "OVER_FUERTE" ? "OVER" :
    r.signal_type === "UNDER_FUERTE" ? "UNDER" : "—";
  const getEdge = r => {
    const v = r.edge_peak ?? r.edge_initial;
    return typeof v === "number" ? v : (v != null ? parseFloat(v) : null);
  };

  // Filtrar y ordenar
  const filtered = useMemo(() => {
    let rows = [...signals];

    // FILTRO POR TAB
    if (activeTab === "enviados") {
      rows = rows.filter(r => r.telegram_sent === true
        && (r.result === "WIN" || r.result === "LOSS"));
    } else if (activeTab === "descartados") {
      rows = rows.filter(r => r.telegram_sent !== true);
    }
    // activeTab === "todos": sin filtro de tab, muestra todo

    if (filterLiga !== "Todas") rows = rows.filter(r => r.liga === filterLiga);
    if (filterResult !== "Todos") rows = rows.filter(r => r.result === filterResult);
    if (filterStake !== "Todos") {
      const stakeNum = parseFloat(filterStake);
      rows = rows.filter(r => parseFloat(r.stake_u_publicado || 0) === stakeNum);
    }
    if (filterPeriodo !== "Todos") {
      const now = Date.now();
      const days = filterPeriodo === "7d" ? 7 : filterPeriodo === "30d" ? 30 : 90;
      const cutoff = now - days * 24 * 60 * 60 * 1000;
      rows = rows.filter(r => getTs(r) >= cutoff);
    }

    if (sortBy === "started_at_desc") {
      rows.sort((a, b) => getTs(b) - getTs(a));
    } else if (sortBy === "edge_desc") {
      rows.sort((a, b) => (getEdge(b) ?? -Infinity) - (getEdge(a) ?? -Infinity));
    }

    return rows;
  }, [signals, activeTab, filterLiga, filterResult, filterStake, filterPeriodo, sortBy]);

  // Conteos por tab (sobre signals raw, ignorando filtros adicionales)
  const tabCounts = useMemo(() => {
    const enviados = signals.filter(r => r.telegram_sent === true
      && (r.result === "WIN" || r.result === "LOSS")).length;
    const descartados = signals.filter(r => r.telegram_sent !== true).length;
    return { enviados, todos: signals.length, descartados };
  }, [signals]);

  // Métricas
  const metrics = useMemo(() => {
    const resueltos = filtered.filter(r => r.result === "WIN" || r.result === "LOSS");
    const wins = resueltos.filter(r => r.result === "WIN").length;
    const losses = resueltos.filter(r => r.result === "LOSS").length;
    const wr = resueltos.length > 0 ? (wins / resueltos.length * 100) : 0;

    const yieldOf = r => {
      const stake = parseFloat(r.stake_u_publicado) || 1;
      return r.result === "WIN" ? stake * 0.91 : -stake;
    };

    const yieldTotal = resueltos.reduce((acc, r) => acc + yieldOf(r), 0);

    // yields 7d/30d se calculan sobre signals enviados (independiente de tab)
    // para preservar la métrica histórica visible incluso en tabs "todos"/"descartados".
    const now = Date.now();
    const sentResolved = signals.filter(r =>
      r.telegram_sent === true && (r.result === "WIN" || r.result === "LOSS"));
    const yieldPeriod = days => {
      const cutoff = now - days * 24 * 60 * 60 * 1000;
      return sentResolved
        .filter(r => getTs(r) >= cutoff)
        .reduce((acc, r) => acc + yieldOf(r), 0);
    };

    return {
      n: filtered.length,
      n_resueltos: resueltos.length,
      sinResolver: filtered.length - resueltos.length,
      wins,
      losses,
      wr,
      yieldTotal,
      yield7d: yieldPeriod(7),
      yield30d: yieldPeriod(30),
    };
  }, [filtered, signals]);

  // Lista de ligas únicas
  const ligas = useMemo(() => {
    const set = new Set(signals.map(r => r.liga).filter(Boolean));
    return ["Todas", ...Array.from(set).sort()];
  }, [signals]);

  // Export Excel (CSV con BOM)
  const exportToExcel = () => {
    const headers = [
      "Fecha", "Liga", "Partido", "Pick", "Edge", "Stake u",
      "Resultado", "Línea", "Total real", "Diferencia",
      "Period", "Clock", "Confianza", "Closed via"
    ];
    const rows = filtered.map(r => {
      const ts = getTs(r);
      const fecha = new Date(ts).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
      const edge = getEdge(r);
      const dif = (typeof r.total_real === "number" && typeof r.line === "number")
        ? (r.total_real - r.line).toFixed(1) : (r.diferencia ?? "");
      return [
        fecha,
        r.liga || "",
        r.partido || "",
        getPick(r),
        edge != null ? edge : "",
        r.stake_u_publicado || "",
        r.result || "",
        r.line ?? "",
        r.total_real ?? "",
        dif,
        r.period ?? "",
        r.clock ?? "",
        r.confianza ?? "",
        r.closed_via ?? "",
      ];
    });
    const csv = [headers, ...rows]
      .map(row => row.map(cell => {
        const s = String(cell ?? "");
        return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","))
      .join("\n");
    const bom = "﻿";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `edge-elite-resultados-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="rt-loading">Cargando resultados...</div>;
  if (error) return <div className="rt-error">{error}</div>;

  return (
    <div className="rt-wrap">

      <div className="rt-header">
        <div>
          <h1>Resultados totales</h1>
          <div className="rt-sub">Histórico completo del motor V6 — todos los picks enviados a Telegram</div>
        </div>
        <button className="rt-export-btn" onClick={exportToExcel}>
          📥 Exportar a Excel
        </button>
      </div>

      {/* TABS */}
      <div className="rt-tabs">
        <button
          className={"rt-tab " + (activeTab === "enviados" ? "active" : "")}
          onClick={() => setActiveTab("enviados")}
        >
          📩 Enviados a Telegram ({tabCounts.enviados})
        </button>
        <button
          className={"rt-tab " + (activeTab === "todos" ? "active" : "")}
          onClick={() => setActiveTab("todos")}
        >
          🔍 Todos los signals ({tabCounts.todos})
        </button>
        <button
          className={"rt-tab " + (activeTab === "descartados" ? "active" : "")}
          onClick={() => setActiveTab("descartados")}
        >
          📊 Descartados / Bloqueados ({tabCounts.descartados})
        </button>
      </div>

      {/* MÉTRICAS RESUMEN */}
      <div className="rt-stats-grid">
        <div className="rt-stat">
          <div className="rt-stat-lbl">Total picks</div>
          <div className="rt-stat-num">{metrics.n}</div>
          <div className="rt-stat-sub">resueltos</div>
        </div>
        <div className="rt-stat green">
          <div className="rt-stat-lbl">Ganadas</div>
          <div className="rt-stat-num">{metrics.wins}</div>
        </div>
        <div className="rt-stat red">
          <div className="rt-stat-lbl">Perdidas</div>
          <div className="rt-stat-num">{metrics.losses}</div>
        </div>
        <div className="rt-stat">
          <div className="rt-stat-lbl">Win rate</div>
          <div className="rt-stat-num">{metrics.wr.toFixed(1)}%</div>
        </div>
        <div className={"rt-stat " + (metrics.yieldTotal >= 0 ? "green" : "red")}>
          <div className="rt-stat-lbl">Yield total</div>
          <div className="rt-stat-num">{metrics.yieldTotal >= 0 ? "+" : ""}{metrics.yieldTotal.toFixed(2)}u</div>
        </div>
        <div className={"rt-stat " + (metrics.yield30d >= 0 ? "green" : "red")}>
          <div className="rt-stat-lbl">Yield 30d</div>
          <div className="rt-stat-num">{metrics.yield30d >= 0 ? "+" : ""}{metrics.yield30d.toFixed(2)}u</div>
        </div>
        <div className={"rt-stat " + (metrics.yield7d >= 0 ? "green" : "red")}>
          <div className="rt-stat-lbl">Yield 7d</div>
          <div className="rt-stat-num">{metrics.yield7d >= 0 ? "+" : ""}{metrics.yield7d.toFixed(2)}u</div>
        </div>
        {activeTab !== "enviados" && (
          <div className="rt-stat">
            <div className="rt-stat-lbl">Sin resolver</div>
            <div className="rt-stat-num">{metrics.sinResolver}</div>
            <div className="rt-stat-sub">aún en curso o sin result</div>
          </div>
        )}
      </div>

      {/* FILTROS */}
      <div className="rt-filters">
        <div className="rt-filter">
          <label>Liga</label>
          <select value={filterLiga} onChange={e => setFilterLiga(e.target.value)}>
            {ligas.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="rt-filter">
          <label>Período</label>
          <select value={filterPeriodo} onChange={e => setFilterPeriodo(e.target.value)}>
            <option value="Todos">Todos</option>
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
          </select>
        </div>
        <div className="rt-filter">
          <label>Stake</label>
          <select value={filterStake} onChange={e => setFilterStake(e.target.value)}>
            <option value="Todos">Todos</option>
            <option value="2">2u</option>
            <option value="1.5">1.5u</option>
            <option value="1">1u</option>
          </select>
        </div>
        <div className="rt-filter">
          <label>Resultado</label>
          <select value={filterResult} onChange={e => setFilterResult(e.target.value)}>
            <option value="Todos">Todos</option>
            <option value="WIN">Ganadas</option>
            <option value="LOSS">Perdidas</option>
          </select>
        </div>
        <div className="rt-filter">
          <label>Ordenar</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="started_at_desc">Más recientes</option>
            <option value="edge_desc">Mayor edge</option>
          </select>
        </div>
        <div className="rt-filter-count">
          Mostrando {filtered.length} de {signals.length}
        </div>
      </div>

      {/* TABLA */}
      <div className="rt-table">
        <div className="rt-row rt-head">
          <div>Fecha</div>
          <div>Liga</div>
          <div>Partido</div>
          <div>Pick</div>
          <div>Edge</div>
          <div>Stake</div>
          <div>Línea</div>
          <div>Total</div>
          <div>Dif</div>
          <div>Resultado</div>
        </div>
        {filtered.map(r => {
          const ts = getTs(r);
          const fecha = new Date(ts).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
          const edge = getEdge(r);
          const dif = (typeof r.total_real === "number" && typeof r.line === "number")
            ? (r.total_real - r.line).toFixed(1) : (r.diferencia ?? "—");
          return (
            <div key={r.id} className="rt-row">
              <div className="rt-cell-date">{fecha}</div>
              <div className="rt-cell-liga">{r.liga || "—"}</div>
              <div className="rt-cell-match">{r.partido || "—"}</div>
              <div className="rt-cell-pick">{getPick(r)}</div>
              <div className="rt-cell-edge">{edge != null ? edge.toFixed(1) : "—"}</div>
              <div>{r.stake_u_publicado || "—"}u</div>
              <div>{r.line ?? "—"}</div>
              <div>{r.total_real ?? "—"}</div>
              <div>{dif}</div>
              <div>
                {r.result === "WIN" || r.result === "LOSS" ? (
                  <span className={"rt-badge " + (r.result === "WIN" ? "win" : "loss")}>
                    {r.result === "WIN" ? "✓ GANADA" : "✗ PERDIDA"}
                  </span>
                ) : (
                  <span className="rt-badge neutral">
                    {r.veredicto || r.categoria || r.telegram_skip_reason || (r.telegram_sent ? "PENDING" : "DESCARTADO")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="rt-empty">No hay resultados con esos filtros</div>
        )}
      </div>

    </div>
  );
}
